/**
 * EmailComponentsTab.tsx
 *
 * The account's library of reusable, pre-built email blocks — the "Email
 * Components" tab under Marketing → Templates. Each component is authored with
 * the same DnD editor used for the default header/footer, and every one shows up
 * in the DnD editor's "Saved" palette (and the HTML editor's insert bar)
 * alongside Header and Footer.
 *
 * jsonContent (the DnD model) is the source of truth; htmlContent is the
 * compiled artifact the HTML editor inserts. Both are always saved together.
 */
import { useState, useMemo, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Blocks, Loader2, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Switch } from '@/components/ui/Switch';
import { FormField } from '@/components/ui/FormField';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import * as marketingSvc from '@/services/marketing.service';
import type { EmailComponent } from '@/services/marketing.service';
import type { DndEmailModel } from '@/components/dnd-editor-v2/dnd-model';
import { toDndModel } from '@/components/dnd-editor-v2/dnd-saved-blocks';
import { TemplateCard } from './TemplateCard';

const DndEditor = lazyWithRetry(
  () => import('@/components/dnd-editor-v2/DndEditor'),
  'dnd-editor-v2',
);

/** Surface the API's own message (validation, 404, …) if it sent one. */
function apiErrorMessage(err: unknown, fallback: string): string {
  const res = (err as { response?: { data?: { error?: { message?: string }; message?: string } } })
    ?.response?.data;
  return res?.error?.message ?? res?.message ?? fallback;
}

const QUERY_KEY = ['marketing', 'email-components'] as const;

export default function EmailComponentsTab() {
  const toast = useToast();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<EmailComponent | null>(null);
  /** The component currently open in the full-screen editor. */
  const [editing, setEditing] = useState<EmailComponent | null>(null);
  /** The component whose name/description/enabled state is being edited. */
  const [details, setDetails] = useState<EmailComponent | null>(null);
  const [detailsName, setDetailsName] = useState('');
  const [detailsDescription, setDetailsDescription] = useState('');
  const [detailsActive, setDetailsActive] = useState(true);

  // The list needs htmlContent for the thumbnail but not the (much larger) DnD
  // model — that's fetched per component when the editor opens.
  const { data, isLoading } = useQuery({
    queryKey: [...QUERY_KEY, 'list'],
    queryFn: () => marketingSvc.listEmailComponents(),
  });

  const components = data?.data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return components;
    return components.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.description ?? '').toLowerCase().includes(q),
    );
  }, [components, search]);

  /** Invalidate the whole family so open editors pick up the new library too. */
  const invalidate = () => qc.invalidateQueries({ queryKey: QUERY_KEY });

  const createMut = useMutation({
    mutationFn: (d: marketingSvc.SaveEmailComponentData) => marketingSvc.createEmailComponent(d),
    onSuccess: (res, vars) => {
      invalidate();
      setCreateOpen(false);
      setNewName('');
      setNewDescription('');
      toast.success('Component created');
      // Drop straight into the editor for a brand-new (empty) component.
      if (!vars.jsonContent) {
        setEditing({
          emailComponentId: res.emailComponentId,
          name: vars.name ?? 'Untitled',
          description: vars.description ?? '',
          htmlContent: '',
          jsonContent: null,
          active: true,
          sortOrder: 0,
          createdAt: '',
          updatedAt: '',
        });
      }
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to create component')),
  });

  const saveContentMut = useMutation({
    mutationFn: ({ id, model, html }: { id: number; model: DndEmailModel; html: string }) =>
      marketingSvc.updateEmailComponent(id, { jsonContent: model, htmlContent: html }),
    onSuccess: () => {
      invalidate();
      toast.success('Component saved');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to save component')),
  });

  // Details only — deliberately does not send jsonContent/htmlContent, so saving
  // a rename can never clobber the component's authored content.
  const saveDetailsMut = useMutation({
    mutationFn: ({ id, ...d }: { id: number } & marketingSvc.SaveEmailComponentData) =>
      marketingSvc.updateEmailComponent(id, d),
    onSuccess: () => {
      invalidate();
      setDetails(null);
      toast.success('Component updated');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to update component')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => marketingSvc.deleteEmailComponent(id),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      toast.success('Component deleted');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to delete component')),
  });

  /**
   * Open the editor for an existing component. The list response omits
   * jsonContent, so fetch the full row first — opening with the list row would
   * hand the editor an empty model and a save would wipe the component.
   */
  async function openEditor(row: EmailComponent) {
    try {
      const full = await marketingSvc.getEmailComponentFull(row.emailComponentId);
      setEditing(full);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to load component'));
    }
  }

  async function duplicate(row: EmailComponent) {
    try {
      const full = await marketingSvc.getEmailComponentFull(row.emailComponentId);
      createMut.mutate({
        name: `${full.name} (Copy)`,
        description: full.description ?? '',
        jsonContent: full.jsonContent,
        htmlContent: full.htmlContent ?? '',
      });
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to duplicate component'));
    }
  }

  function handleSaveContent(model: DndEmailModel, html: string) {
    if (!editing) return;
    saveContentMut.mutate({ id: editing.emailComponentId, model, html });
  }

  function openDetails(row: EmailComponent) {
    setDetailsName(row.name);
    setDetailsDescription(row.description ?? '');
    setDetailsActive(row.active);
    setDetails(row);
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Toolbar */}
      <div className="px-5 pt-1 pb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-slate-500">
            Reusable blocks available in the email builder under <strong>Saved</strong>.
          </p>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search components..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 w-52"
              />
            </div>
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
              New Component
            </Button>
          </div>
        </div>
      </div>

      {/* Card grid */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Blocks className="h-16 w-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No components found</h3>
            <p className="text-slate-400 mb-4">
              {search
                ? 'Try a different search'
                : 'Build a reusable block once, then drop it into any email from the builder’s Saved palette'}
            </p>
            {!search && (
              <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
                Create Component
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-5">
            {filtered.map((c) => (
              <TemplateCard
                key={c.emailComponentId}
                name={c.name}
                active={c.active}
                meta={c.description || 'No description'}
                preview={
                  c.htmlContent ? (
                    <div className="w-full h-full overflow-hidden relative">
                      <iframe
                        srcDoc={c.htmlContent}
                        className="absolute top-0 left-0 border-none pointer-events-none"
                        style={{
                          width: '600px',
                          height: '800px',
                          transform: 'scale(0.47)',
                          transformOrigin: 'top left',
                        }}
                        sandbox="allow-same-origin"
                        title={c.name}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Blocks className="h-8 w-8 text-slate-200" />
                    </div>
                  )
                }
                onEdit={() => openEditor(c)}
                onDuplicate={() => duplicate(c)}
                extraActions={[
                  { icon: Settings2, title: 'Details', onClick: () => openDetails(c) },
                ]}
                onDelete={() => setDeleteTarget(c)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create modal — name/description first, then straight into the editor */}
      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Email Component"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMut.mutate({ name: newName.trim(), description: newDescription.trim() })}
              disabled={!newName.trim() || createMut.isPending}
            >
              Create &amp; Edit
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Name" required>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Hero banner"
              autoFocus
            />
          </FormField>
          <FormField label="Description">
            <Textarea autoResize
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Shown as the tooltip in the builder's block palette"
              rows={3}
            />
          </FormField>
        </div>
      </Modal>

      {/* Details modal — rename / describe / enable. Content is edited separately
          in the full-screen builder, so this never touches json/htmlContent. */}
      <Modal
        isOpen={details !== null}
        onClose={() => setDetails(null)}
        title="Component Details"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDetails(null)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                details &&
                saveDetailsMut.mutate({
                  id: details.emailComponentId,
                  name: detailsName.trim(),
                  description: detailsDescription.trim(),
                  active: detailsActive,
                })
              }
              disabled={!detailsName.trim() || saveDetailsMut.isPending}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Name" required>
            <Input value={detailsName} onChange={(e) => setDetailsName(e.target.value)} />
          </FormField>
          <FormField label="Description">
            <Textarea autoResize
              value={detailsDescription}
              onChange={(e) => setDetailsDescription(e.target.value)}
              placeholder="Shown as the tooltip in the builder's block palette"
              rows={3}
            />
          </FormField>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-slate-700">Available in the builder</div>
              <p className="text-xs text-slate-500 mt-0.5">
                When off, the component is kept here but withheld from the email builder’s
                Saved palette.
              </p>
            </div>
            <Switch checked={detailsActive} onChange={setDetailsActive} size="md" />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Delete Component"
        message={
          deleteTarget
            ? `Delete "${deleteTarget.name}"? It will no longer be available in the email builder. Emails that already use it are unaffected.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (deleteTarget) deleteMut.mutate(deleteTarget.emailComponentId);
        }}
        onClose={() => setDeleteTarget(null)}
      />

      {/* Full-screen DnD editor, portaled out of the tab panel.
          We intentionally do NOT pass `savedBlocks` here: a component must not be
          able to embed itself (or the header/footer) — same reasoning as the
          header/footer editor in MarketingSettingsPage. */}
      {editing &&
        createPortal(
          <div className="fixed inset-0 bg-slate-100 z-[9999]">
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              }
            >
              <DndEditor
                key={editing.emailComponentId}
                initialModel={toDndModel(editing.jsonContent)}
                initialFullscreen
                onSave={handleSaveContent}
                onClose={() => setEditing(null)}
              />
            </Suspense>
          </div>,
          document.body,
        )}
    </div>
  );
}
